import { LoadingButton } from '@mui/lab';
import { Button, Stack, Typography } from '@mui/material';
import { PromptDialogOnSide } from 'common/components/PromptDialogOnSide/PromptDialogOnSide';
import { styles } from 'common/components/PromptDialogOnSide/PromptDialogOnSide.styles';
import { DialogComponentProps } from 'common/hooks/useDialog';
import { useNotification } from 'common/hooks/useNotification';
import { useDeleteCashFlowAdjustmentMutation } from 'generated/graphql';
import { useTranslation } from 'react-i18next';

import { CashFlowAdjustmentType } from '../AddCashFlowAdjustmentDialog/AddCashFlowAdjustmentDialog';

export interface DeleteCashFlowAdjustmentDialogProps extends DialogComponentProps<boolean> {
  id: string;
  type: CashFlowAdjustmentType;
}

export const DeleteCashFlowAdjustmentDialog: React.FC<DeleteCashFlowAdjustmentDialogProps> = ({
  id,
  isOpen,
  onResolve,
  type,
}) => {
  const { t } = useTranslation();
  const notify = useNotification();

  const [deleteCashFlowAdjustment, { loading }] = useDeleteCashFlowAdjustmentMutation({
    onCompleted: (resp) => {
      if (resp.deleteCashFlowAdjustment) {
        onResolve(true);
      }
    },
    onError: () => {
      notify({ message: t('common:somethingWentWrong'), type: 'error' });
      onResolve(false);
    },
    refetchQueries: ['CashFlowAdjustments'],
  });

  const handleDelete = async () => {
    if (!loading) {
      await deleteCashFlowAdjustment({ variables: { id } });
    }
  };

  return (
    <PromptDialogOnSide
      onClose={() => onResolve(false)}
      open={isOpen}
      title={
        type == CashFlowAdjustmentType.Income
          ? t('fintelligenceReport.deleteCashFlowAdjustmentDialog.income.title')
          : t('fintelligenceReport.deleteCashFlowAdjustmentDialog.expense.title')
      }
    >
      <Stack spacing={1}>
        <LoadingButton color="tertiaryButton" loading={loading} onClick={handleDelete} sx={styles.button}>
          <Typography color="red.dark" variant="bodyL">
            {t('fintelligenceReport.deleteCashFlowAdjustmentDialog.button')}
          </Typography>
        </LoadingButton>
        <Button color="tertiaryButton" onClick={() => onResolve(false)} sx={styles.button}>
          <Typography color="blue.dark" variant="bodyStrongL">
            {t('common:cancel')}
          </Typography>
        </Button>
      </Stack>
    </PromptDialogOnSide>
  );
};
